<template>
  <div class="w-full md:px-8 py-6">
    <!-- Header -->
    <div class="text-center mb-8">
      <h1 class="text-xl font-medium text-gray-900 mb-1">Finalizar Pagamento</h1>
      <p class="text-sm text-gray-500">Preencha os dados para concluir a compra</p>
    </div>

    <!-- Buyer Information -->
    <div class="mb-8">
      <div class="text-sm mb-4 text-gray-900">Dados do Comprador</div>

      <!-- Full Name -->
      <div class="mb-4">
        <div class="mb-2 text-sm">Nome Completo <span class="text-red-500">*</span></div>
        <input
          type="text"
          :value="customerData.name"
          disabled
          class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4"
        >
      </div>

      <!-- Email and Phone -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <div class="mb-2 text-sm">E-mail <span class="text-red-500">*</span></div>
          <input
            type="email"
            :value="customerData.email"
            disabled
            class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4"
          >
        </div>
        <div>
          <div class="mb-2 text-sm">Telefone <span class="text-red-500">*</span></div>
          <input
            type="tel"
            :value="customerData.phone"
            disabled
            class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4"
          >
        </div>
      </div>

      <!-- CPF/CNPJ -->
      <div>
        <div class="mb-2 text-sm">CPF/CNPJ <span class="text-red-500">*</span></div>
        <input
          v-model="additionalData.cpf"
          type="text"
          placeholder="000.000.000-00"
          maxlength="18"
          @input="formatCpfCnpj"
          class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
          :class="{ 'border-red-500': !additionalData.cpf && error }"
        >
        <p v-if="!additionalData.cpf && error" class="text-red-500 text-sm mt-1">CPF é obrigatório para pagamento</p>
      </div>
    </div>

    <!-- Discount Coupon -->
    <div class="mb-8">
      <div class="text-sm mb-4 text-gray-900">Cupom de Desconto</div>

      <!-- Cupom aplicado -->
      <div v-if="appliedCoupon" class="flex items-center justify-between bg-green-50 border border-green-200 rounded-md p-3">
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          <div>
            <div class="text-sm font-semibold text-green-800">{{ appliedCoupon.code }}</div>
            <div class="text-xs text-green-700">{{ appliedCoupon.discount }}% de desconto aplicado</div>
          </div>
        </div>
        <button
          type="button"
          @click="removeCoupon"
          class="text-xs font-medium text-red-600 hover:text-red-800"
          :disabled="processing"
        >
          Remover
        </button>
      </div>

      <!-- Form de cupom -->
      <div v-else>
        <div class="flex gap-3">
          <input
            v-model="couponCode"
            type="text"
            placeholder="Digite o código do cupom"
            class="flex-1 bg-white border border-gray-300 text-gray-900 text-sm rounded-md p-4 focus:outline-none focus:ring-1 focus:ring-gray-200 uppercase"
            :disabled="couponValidating"
            @keyup.enter="applyCoupon"
          >
          <button
            type="button"
            @click="applyCoupon"
            :disabled="couponValidating || !couponCode.trim()"
            class="px-6 p-4 checkout-btn-primary rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ couponValidating ? 'Validando...' : 'Aplicar' }}
          </button>
        </div>
        <p v-if="couponError" class="mt-2 text-xs text-red-600">{{ couponError }}</p>
      </div>
    </div>

    <!-- Free order notice (100% off) -->
    <div v-if="isFreeOrder" class="mb-8 p-4 bg-green-50 border border-green-200 rounded-md text-center">
      <p class="text-sm text-green-800 font-semibold mb-1">Inscrição 100% Gratuita</p>
      <p class="text-xs text-green-700">Cupom aplicado cobre o valor total. Clique abaixo para confirmar sua inscrição.</p>
    </div>

    <!-- Payment Method -->
    <div v-if="!isFreeOrder" class="mb-8">
      <div class="text-sm mb-4 text-gray-900">Método de Pagamento</div>

      <div class="grid grid-cols-3 gap-4 mb-6">
        <!-- Credit Card -->
        <button
          type="button"
          @click="activePayment = 'creditcard'"
          class="payment-btn"
          :class="{ 'active': activePayment === 'creditcard' }"
        >
          <svg class="w-5 h-5 mx-auto mb-1.5" viewBox="0 0 24 24" fill="currentColor">
            <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
            <path d="M2 9h20" stroke="currentColor" stroke-width="2"/>
          </svg>
          <span class="text-xs font-medium">Cartão de Crédito</span>
        </button>

        <!-- Pix -->
        <button
          type="button"
          @click="activePayment = 'pix'"
          class="payment-btn"
          :class="{ 'active': activePayment === 'pix' }"
        >
          <svg class="w-5 h-5 mx-auto mb-1.5" viewBox="0 0 22 23" fill="currentColor">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.19223 5.24323C6.06969 5.24323 6.89487 5.58498 7.51525 6.20516L10.8818 9.57225C11.1243 9.8147 11.5202 9.81575 11.7633 9.57193L15.1175 6.21736C15.738 5.59718 16.5632 5.25554 17.4407 5.25554H17.8447L13.5842 0.995107C12.2574 -0.331702 10.1063 -0.331702 8.77948 0.995107L4.53135 5.24323H5.19223ZM17.4406 17.108C16.5632 17.108 15.738 16.7664 15.1176 16.1462L11.7632 12.792C11.5278 12.5558 11.1173 12.5565 10.8819 12.792L7.51531 16.1585C6.89482 16.7786 6.06964 17.1202 5.19219 17.1202H4.5312L8.77943 21.3686C10.1062 22.6953 12.2574 22.6953 13.5842 21.3686L17.8447 17.108H17.4406ZM18.794 6.20484L21.3686 8.77947C22.6954 10.1062 22.6954 12.2573 21.3686 13.5842L18.7941 16.1587C18.7373 16.1359 18.6761 16.1218 18.6112 16.1218H17.4407C16.8354 16.1218 16.243 15.8764 15.8154 15.4484L12.4611 12.0945C11.8532 11.4859 10.7925 11.4862 10.184 12.0942L6.81744 15.4607C6.38976 15.8886 5.79746 16.134 5.19222 16.134H3.75286C3.69154 16.134 3.634 16.1486 3.57983 16.169L0.995108 13.5842C-0.331703 12.2573 -0.331703 10.1062 0.995108 8.77947L3.57994 6.19464C3.63411 6.21504 3.69154 6.22956 3.75286 6.22956H5.19222C5.79746 6.22956 6.38976 6.47496 6.81744 6.90285L10.1843 10.2697C10.4982 10.5833 10.9103 10.7404 11.3227 10.7404C11.7349 10.7404 12.1473 10.5833 12.4611 10.2694L15.8154 6.91505C16.243 6.48716 16.8354 6.24176 17.4407 6.24176H18.6112C18.676 6.24176 18.7373 6.22756 18.794 6.20484Z"/>
          </svg>
          <span class="text-xs font-medium">Pix</span>
        </button>

        <!-- Boleto -->
        <button
          type="button"
          @click="activePayment = 'boleto'"
          class="payment-btn"
          :class="{ 'active': activePayment === 'boleto' }"
        >
          <svg class="w-5 h-5 mx-auto mb-1.5" fill="currentColor" viewBox="0 0 297 297">
            <g>
              <rect width="33" y="49.5" height="198"/>
              <rect width="33" x="83" y="49.5" height="198"/>
              <rect width="16" x="50" y="49.5" height="198"/>
            </g>
          </svg>
          <span class="text-xs font-medium">Boleto</span>
        </button>
      </div>

      <!-- Info badge for PIX/Boleto -->
      <div v-if="!isFreeOrder && (activePayment === 'pix' || activePayment === 'boleto')" class="checkout-info-badge mb-6">
        <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
        </svg>
        <span>
          Clique em <strong>Finalizar Pagamento</strong> abaixo para gerar o {{ activePayment === 'pix' ? 'codigo PIX' : 'boleto' }}.
        </span>
      </div>

      <!-- Credit Card Form -->
      <div v-if="!isFreeOrder && activePayment === 'creditcard'" class="space-y-4">
        <!-- Card Number -->
        <div>
          <div class="mb-2 text-sm">Número do Cartão <span class="text-red-500">*</span></div>
          <div class="relative">
            <input
              v-model="cardData.number"
              type="text"
              placeholder="0000 0000 0000 0000"
              maxlength="19"
              @input="formatCardNumber"
              class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
              :class="{ 'border-red-500': cardErrors.number }"
            >
            <div class="absolute right-4 top-4 flex gap-1.5 items-center">
              <svg class="h-4" viewBox="0 0 48 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.87 1.01l-3.15 13.98h-2.52L15.35 1.01h2.52zm12.64 9.02l1.33-3.67.76 3.67h-2.09zm2.82 4.96h2.33L33.6 1.01h-2.15a1.14 1.14 0 00-1.07.71l-3.76 13.27h2.63l.52-1.44h3.21l.3 1.44h.05zm-6.5-4.57c.01-3.68-5.08-3.88-5.05-5.53.01-.5.49-1.03 1.53-1.17a6.8 6.8 0 013.56.62l.63-2.96A9.7 9.7 0 0024.02.8c-2.48 0-4.22 1.32-4.23 3.2-.02 1.4 1.24 2.17 2.19 2.64.97.48 1.3.78 1.29 1.21-.01.65-.77.94-1.49.95-1.25.02-1.97-.34-2.55-.61l-.45 2.1c.58.27 1.65.5 2.76.51 2.63 0 4.35-1.3 4.36-3.32l.01.02h-.02zM14.02 1.01L10.11 15h-2.7L5.56 3.82c-.11-.45-.21-.61-.56-.8C4.32 2.65 3.22 2.31 2.24 2.1l.06-.26h4.26c.54 0 1.03.36 1.15.99l1.06 5.6 2.61-6.59h2.64v.17z" fill="#1A1F71"/></svg>
              <svg class="h-4" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="10" r="8" fill="#EB001B"/><circle cx="20" cy="10" r="8" fill="#F79E1B"/><path d="M16 3.6a8 8 0 010 12.8 8 8 0 000-12.8z" fill="#FF5F00"/></svg>
            </div>
          </div>
          <p v-if="cardErrors.number" class="text-red-500 text-sm mt-1">{{ cardErrors.number }}</p>
        </div>

        <!-- Cardholder Name -->
        <div>
          <div class="mb-2 text-sm">Nome do Titular <span class="text-red-500">*</span></div>
          <input
            v-model="cardData.holderName"
            type="text"
            placeholder="Nome como está no cartão"
            class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
            :class="{ 'border-red-500': cardErrors.holderName }"
          >
          <p v-if="cardErrors.holderName" class="text-red-500 text-sm mt-1">{{ cardErrors.holderName }}</p>
        </div>

        <!-- Expiry and CVV -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <div class="mb-2 text-sm">Validade <span class="text-red-500">*</span></div>
            <input
              v-model="cardData.expiry"
              type="text"
              placeholder="MM/AA"
              maxlength="5"
              @input="formatExpiry"
              class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
              :class="{ 'border-red-500': cardErrors.expiry }"
            >
            <p v-if="cardErrors.expiry" class="text-red-500 text-sm mt-1">{{ cardErrors.expiry }}</p>
          </div>
          <div>
            <div class="mb-2 text-sm">CVV <span class="text-red-500">*</span></div>
            <input
              v-model="cardData.cvv"
              type="text"
              placeholder="000"
              maxlength="4"
              @input="formatCvv"
              class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
              :class="{ 'border-red-500': cardErrors.cvv }"
            >
            <p v-if="cardErrors.cvv" class="text-red-500 text-sm mt-1">{{ cardErrors.cvv }}</p>
          </div>
        </div>

        <!-- Installments -->
        <div>
          <div class="mb-2 text-sm">Parcelamento <span class="text-red-500">*</span></div>
          <select
            v-model="cardData.installments"
            class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
          >
            <option v-for="inst in installmentOptions" :key="inst.number" :value="inst.number">
              {{ inst.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Billing Address Section (for Credit Card and Boleto) -->
      <div v-if="!isFreeOrder && (activePayment === 'creditcard' || activePayment === 'boleto')">
        <div class="mt-6 pt-6 border-t border-gray-200">
          <div class="text-sm mb-4 text-gray-900">Endereço de Cobrança</div>

          <!-- ZIP Code -->
          <div class="mb-4">
            <div class="mb-2 text-sm">CEP <span class="text-red-500">*</span></div>
            <div class="relative">
              <input
                v-model="billingAddress.zipCode"
                type="text"
                placeholder="00000-000"
                maxlength="9"
                @input="formatZipCode"
                @blur="searchCEP"
                :disabled="loadingCEP"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                :class="{ 'border-red-500': cardErrors.zipCode, 'bg-gray-100': loadingCEP }"
              >
              <div v-if="loadingCEP" class="absolute right-4 top-4">
                <svg class="animate-spin h-5 w-5 checkout-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <p v-if="cardErrors.zipCode" class="text-red-500 text-sm mt-1">{{ cardErrors.zipCode }}</p>
          </div>

          <!-- Street Address and Number -->
          <div class="grid grid-cols-4 gap-4 mb-4">
            <div class="col-span-3">
              <div class="mb-2 text-sm">Endereço <span class="text-red-500">*</span></div>
              <input
                v-model="billingAddress.line1"
                type="text"
                placeholder="Rua"
                :disabled="loadingCEP"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                :class="{ 'border-red-500': cardErrors.line1, 'bg-gray-100': loadingCEP }"
              >
              <p v-if="cardErrors.line1" class="text-red-500 text-sm mt-1">{{ cardErrors.line1 }}</p>
            </div>
            <div>
              <div class="mb-2 text-sm">Nº <span class="text-red-500">*</span></div>
              <input
                v-model="billingAddress.number"
                type="text"
                placeholder="123"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                :class="{ 'border-red-500': cardErrors.addressNumber }"
              >
              <p v-if="cardErrors.addressNumber" class="text-red-500 text-sm mt-1">{{ cardErrors.addressNumber }}</p>
            </div>
          </div>

          <!-- Neighborhood -->
          <div class="mb-4">
            <div class="mb-2 text-sm">Bairro <span class="text-red-500">*</span></div>
            <input
              v-model="billingAddress.neighborhood"
              type="text"
              placeholder="Bairro"
              :disabled="loadingCEP"
              class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
              :class="{ 'border-red-500': cardErrors.neighborhood, 'bg-gray-100': loadingCEP }"
            >
            <p v-if="cardErrors.neighborhood" class="text-red-500 text-sm mt-1">{{ cardErrors.neighborhood }}</p>
          </div>

          <!-- City and State -->
          <div class="grid grid-cols-3 gap-4">
            <div class="col-span-2">
              <div class="mb-2 text-sm">Cidade <span class="text-red-500">*</span></div>
              <input
                v-model="billingAddress.city"
                type="text"
                placeholder="Cidade"
                :disabled="loadingCEP"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                :class="{ 'border-red-500': cardErrors.city, 'bg-gray-100': loadingCEP }"
              >
              <p v-if="cardErrors.city" class="text-red-500 text-sm mt-1">{{ cardErrors.city }}</p>
            </div>
            <div>
              <div class="mb-2 text-sm">UF <span class="text-red-500">*</span></div>
              <input
                v-model="billingAddress.state"
                type="text"
                placeholder="SP"
                maxlength="2"
                @input="formatState"
                :disabled="loadingCEP"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                :class="{ 'border-red-500': cardErrors.state, 'bg-gray-100': loadingCEP }"
              >
              <p v-if="cardErrors.state" class="text-red-500 text-sm mt-1">{{ cardErrors.state }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- PIX Display -->
      <div v-if="activePayment === 'pix' && pixData" class="text-center space-y-4 py-2">
        <img :src="pixData.qr_code_url" alt="QR Code PIX" class="max-w-[220px] mx-auto border border-gray-200 rounded-md p-2">
        <div class="p-4 bg-gray-50 border border-gray-200 rounded-md text-sm break-all text-gray-700 font-mono">
          {{ pixData.qr_code }}
        </div>
        <button
          type="button"
          @click="copyPixCode"
          class="px-6 p-4 bg-gray-100 text-gray-900 text-sm rounded-md hover:bg-gray-200 transition-colors"
        >
          Copiar codigo PIX
        </button>
        <!-- Status do polling -->
        <div v-if="pixPollingStatus === 'polling' || pixPollingStatus === 'pending'" class="flex items-center justify-center gap-2 text-sm text-yellow-600">
          <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Aguardando confirmacao do pagamento...
        </div>
        <div v-if="pixPollingStatus === 'paid'" class="text-sm text-green-600 font-semibold">
          Pagamento confirmado!
        </div>
      </div>

      <!-- Boleto Display -->
      <div v-if="activePayment === 'boleto' && boletoData" class="space-y-4 py-2">
        <div class="p-4 bg-gray-50 border border-gray-200 rounded-md text-sm break-all font-mono text-gray-700">
          {{ boletoData.boleto_barcode }}
        </div>
        <a
          :href="boletoData.boleto_url"
          target="_blank"
          class="block text-center p-4 checkout-btn-primary rounded-md text-sm font-semibold"
        >
          Baixar Boleto (PDF)
        </a>
      </div>
    </div>

    <!-- Order Summary -->
    <div class="mb-8 p-5 bg-gray-50 border border-gray-200 rounded-md">
      <div class="text-sm mb-4 text-gray-900">Resumo do Pedido</div>

      <!-- Items -->
      <div class="space-y-3 mb-4">
        <div class="flex justify-between items-start">
          <div>
            <div class="text-sm font-medium text-gray-900">{{ ticketData.name }}</div>
            <div class="text-sm text-gray-500">{{ quantity }} x {{ formatCurrency(ticketData.value || ticketData.amount_ticket) }}</div>
          </div>
          <div class="text-sm font-semibold text-gray-900">{{ formatCurrency(total) }}</div>
        </div>
      </div>

      <!-- Totals -->
      <div class="border-t border-gray-200 pt-4 space-y-2">
        <div class="flex justify-between text-sm text-gray-600">
          <span>Subtotal</span>
          <span>{{ formatCurrency(total) }}</span>
        </div>
        <div v-if="appliedCoupon" class="flex justify-between text-sm text-green-700">
          <span>Desconto ({{ appliedCoupon.code }})</span>
          <span>- {{ formatCurrency(discountAmount) }}</span>
        </div>
        <div v-if="serviceFee > 0" class="flex justify-between text-sm text-gray-600">
          <span>Taxa de serviço</span>
          <span>{{ formatCurrency(serviceFee) }}</span>
        </div>
        <div class="flex justify-between text-lg font-bold text-gray-900 pt-2">
          <span>Total</span>
          <span class="checkout-total">
            <span v-if="isFreeOrder" class="text-green-600">GRATUITO</span>
            <span v-else>{{ formatCurrency(customerTotal) }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Complete Payment Button -->
    <button
      v-if="!pixData && !boletoData"
      type="button"
      @click="handlePayment"
      :disabled="processing"
      class="w-full p-4 checkout-btn-primary text-sm font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
    >
      <svg v-if="!processing" class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
      </svg>
      <span>{{ processing ? 'Processando...' : (isFreeOrder ? 'Confirmar Inscrição Gratuita' : 'Finalizar Pagamento') }}</span>
    </button>

    <p class="text-center text-sm text-gray-400 mt-4 flex items-center justify-center">
      <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
      </svg>
      Pagamento seguro via Pagar.me
    </p>

    <!-- Error Message -->
    <div v-if="error" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
      <p class="text-red-800 text-sm">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { ref, computed, onBeforeUnmount } from 'vue'
import { generatePixPayment, generateBoletoPayment, processCardPayment, processFreePayment, calculateInstallments, monitorPixPayment } from '../../services/api/payment'
import { createCardToken } from '../../services/api/pagarme'
import { consultaCEP } from '../../services/api/consultaCEP'
import { validateCoupon as validateCouponApi } from '../../services/api/coupons'

export default {
  name: 'CheckoutStepNew',
  props: {
    ticketData: {
      type: Object,
      required: true
    },
    quantity: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    participantId: {
      type: [Number, String],
      required: true
    },
    customerData: {
      type: Object,
      required: true
    },
    paymentConfig: {
      type: Object,
      default: null
    }
  },
  emits: ['pix-generated', 'boleto-generated', 'payment-complete'],
  setup(props, { emit }) {
    const activePayment = ref('creditcard')
    const processing = ref(false)
    const error = ref(null)
    const couponCode = ref('')
    const appliedCoupon = ref(null)
    const couponValidating = ref(false)
    const couponError = ref(null)
    const pixData = ref(null)
    const boletoData = ref(null)
    const loadingCEP = ref(false)
    const pixMonitor = ref(null)
    const pixPollingStatus = ref(null)

    // Limpa o polling ao desmontar o componente
    onBeforeUnmount(() => {
      if (pixMonitor.value) {
        pixMonitor.value.stop()
        pixMonitor.value = null
      }
    })

    const additionalData = ref({
      cpf: props.customerData.cpf || ''
    })

    const cardData = ref({
      number: '',
      holderName: '',
      expiry: '',
      cvv: '',
      installments: 1
    })

    const billingAddress = ref({
      zipCode: '',
      line1: '',
      number: '',
      neighborhood: '',
      city: '',
      state: ''
    })

    const cardErrors = ref({})

    const organizerPaysFee = computed(() => {
      return props.paymentConfig?.marketplace_pays_gateway_fee ?? true
    })

    const getCardFeePercent = (installments) => {
      const fees = props.paymentConfig?.card_fees
      if (!fees) return 0
      if (installments <= 1) return parseFloat(fees['1x']) || 0
      if (installments <= 6) return parseFloat(fees['2_6x']) || 0
      return parseFloat(fees['7_12x']) || 0
    }

    // Desconto do cupom aplicado em valor absoluto (R$). Calculado sobre o
    // subtotal (props.total). Se cupom for 100%, effectiveTotal vira 0 e o
    // checkout oferece "Inscrição Gratuita" em vez de gateway de pagamento.
    const discountAmount = computed(() => {
      if (!appliedCoupon.value) return 0
      const pct = parseFloat(appliedCoupon.value.discount) || 0
      return (props.total * pct) / 100
    })

    const effectiveTotal = computed(() => {
      const t = props.total - discountAmount.value
      return t > 0 ? t : 0
    })

    const isFreeOrder = computed(() => effectiveTotal.value === 0)

    const serviceFee = computed(() => {
      if (organizerPaysFee.value || !props.paymentConfig) return 0
      if (isFreeOrder.value) return 0
      const base = effectiveTotal.value
      const txFee = parseFloat(props.paymentConfig.transaction_fee_fixed) || 0
      let total = base

      if (activePayment.value === 'pix') {
        const pixPercent = parseFloat(props.paymentConfig.pix_fee_percentage) || 0
        total = (base + txFee) / (1 - pixPercent / 100)
      } else if (activePayment.value === 'boleto') {
        const boletoFixed = parseFloat(props.paymentConfig.boleto_fee_fixed) || 0
        total = base + boletoFixed + txFee
      } else {
        const cardPercent = getCardFeePercent(cardData.value.installments)
        const antifraud = parseFloat(props.paymentConfig.antifraud_fee_fixed) || 0
        total = (base + txFee + antifraud) / (1 - cardPercent / 100)
      }

      return total - base
    })

    const customerTotal = computed(() => {
      return effectiveTotal.value + serviceFee.value
    })

    const installmentOptions = computed(() => {
      const max = props.paymentConfig?.max_installments || 12
      return calculateInstallments(customerTotal.value, max, 5)
    })

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    }

    const formatCpfCnpj = (event) => {
      let value = event.target.value.replace(/\D/g, '')
      if (value.length <= 11) {
        value = value.replace(/(\d{3})(\d)/, '$1.$2')
        value = value.replace(/(\d{3})(\d)/, '$1.$2')
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      } else {
        value = value.replace(/^(\d{2})(\d)/, '$1.$2')
        value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        value = value.replace(/\.(\d{3})(\d)/, '.$1/$2')
        value = value.replace(/(\d{4})(\d)/, '$1-$2')
      }
      additionalData.value.cpf = value
    }

    const formatCardNumber = (event) => {
      let value = event.target.value.replace(/\s/g, '')
      let formattedValue = value.match(/.{1,4}/g)?.join(' ') || value
      cardData.value.number = formattedValue.substring(0, 19)
    }

    const formatExpiry = (event) => {
      let value = event.target.value.replace(/\D/g, '')
      if (value.length >= 2) {
        value = value.substring(0, 2) + '/' + value.substring(2, 4)
      }
      cardData.value.expiry = value
    }

    const formatCvv = (event) => {
      cardData.value.cvv = event.target.value.replace(/\D/g, '').substring(0, 4)
    }

    const formatZipCode = (event) => {
      let value = event.target.value.replace(/\D/g, '')
      if (value.length <= 8) {
        value = value.replace(/(\d{5})(\d{3})/, '$1-$2')
      }
      billingAddress.value.zipCode = value
    }

    const formatState = (event) => {
      billingAddress.value.state = event.target.value.toUpperCase()
    }

    const searchCEP = async () => {
      const cep = billingAddress.value.zipCode?.replace(/\D/g, '')

      if (!cep || cep.length !== 8) {
        return
      }

      loadingCEP.value = true
      cardErrors.value.zipCode = null

      try {
        const resp = await consultaCEP(cep)
        billingAddress.value.line1 = resp.address || ''
        billingAddress.value.neighborhood = resp.neighborhood || ''
        billingAddress.value.city = resp.city || ''
        billingAddress.value.state = resp.state || ''

        // Limpar erros dos campos preenchidos
        cardErrors.value.line1 = null
        cardErrors.value.neighborhood = null
        cardErrors.value.city = null
        cardErrors.value.state = null
      } catch (err) {
        cardErrors.value.zipCode = 'CEP não encontrado'
        // Limpar os campos se o CEP for inválido
        billingAddress.value.line1 = ''
        billingAddress.value.neighborhood = ''
        billingAddress.value.city = ''
        billingAddress.value.state = ''
      } finally {
        loadingCEP.value = false
      }
    }

    const validateCardForm = () => {
      cardErrors.value = {}
      let isValid = true

      const cardNumber = cardData.value.number.replace(/\s/g, '')
      if (!cardNumber || cardNumber.length < 13) {
        cardErrors.value.number = 'Número do cartão inválido'
        isValid = false
      }

      if (!cardData.value.holderName || cardData.value.holderName.length < 3) {
        cardErrors.value.holderName = 'Nome do titular é obrigatório'
        isValid = false
      }

      const expiry = cardData.value.expiry.split('/')
      if (expiry.length !== 2 || expiry[0].length !== 2 || expiry[1].length !== 2) {
        cardErrors.value.expiry = 'Data de validade inválida (MM/AA)'
        isValid = false
      }

      if (!cardData.value.cvv || cardData.value.cvv.length < 3) {
        cardErrors.value.cvv = 'CVV inválido'
        isValid = false
      }

      if (!additionalData.value.cpf || additionalData.value.cpf.replace(/\D/g, '').length < 11) {
        error.value = 'CPF é obrigatório para pagamento'
        isValid = false
      }

      // Validate billing address
      if (!billingAddress.value.zipCode || billingAddress.value.zipCode.replace(/\D/g, '').length !== 8) {
        cardErrors.value.zipCode = 'CEP é obrigatório'
        isValid = false
      }

      if (!billingAddress.value.line1 || billingAddress.value.line1.length < 3) {
        cardErrors.value.line1 = 'Endereço é obrigatório'
        isValid = false
      }

      if (!billingAddress.value.number) {
        cardErrors.value.addressNumber = 'Número é obrigatório'
        isValid = false
      }

      if (!billingAddress.value.neighborhood || billingAddress.value.neighborhood.length < 2) {
        cardErrors.value.neighborhood = 'Bairro é obrigatório'
        isValid = false
      }

      if (!billingAddress.value.city || billingAddress.value.city.length < 2) {
        cardErrors.value.city = 'Cidade é obrigatória'
        isValid = false
      }

      if (!billingAddress.value.state || billingAddress.value.state.length !== 2) {
        cardErrors.value.state = 'UF é obrigatória'
        isValid = false
      }

      return isValid
    }

    const handleCardPayment = async () => {
      console.log('🚀 Iniciando pagamento com cartão...')
      console.log('📋 Dados do formulário:', {
        cardData: cardData.value,
        billingAddress: billingAddress.value,
        additionalData: additionalData.value
      })

      processing.value = true
      error.value = null
      cardErrors.value = {}

      if (!validateCardForm()) {
        console.error('❌ Validação falhou:', cardErrors.value)
        processing.value = false
        return
      }

      console.log('✅ Validação passou!')

      try {
        const expiry = cardData.value.expiry.split('/')

        console.log('🔐 Criando token do cartão...')
        const cardToken = await createCardToken({
          number: cardData.value.number,
          holderName: cardData.value.holderName,
          expMonth: expiry[0],
          expYear: expiry[1],
          cvv: cardData.value.cvv
        })
        console.log('✅ Token criado:', cardToken)

        console.log('💳 Processando pagamento...')
        const paymentData = await processCardPayment({
          participantId: props.participantId,
          ticketId: props.ticketData.id,
          quantity: props.quantity,
          amount: effectiveTotal.value,
          couponId: appliedCoupon.value?.id || null,
          installments: cardData.value.installments,
          card: { token: cardToken },
          billingAddress: {
            number: billingAddress.value.number,
            address: billingAddress.value.line1,
            neighborhood: billingAddress.value.neighborhood,
            zipCode: billingAddress.value.zipCode.replace(/\D/g, ''),
            city: billingAddress.value.city,
            state: billingAddress.value.state
          },
          customer: {
            name: props.customerData.name,
            email: props.customerData.email,
            document: additionalData.value.cpf.replace(/\D/g, ''),
            phone: props.customerData.phone
          }
        })

        console.log('✅ Pagamento processado com sucesso:', paymentData)
        emit('payment-complete', paymentData)
      } catch (err) {
        console.error('❌ Erro no pagamento:', err)
        error.value = err.message || 'Erro ao processar pagamento. Tente novamente.'
      } finally {
        processing.value = false
      }
    }

    const generatePix = async () => {
      if (!additionalData.value.cpf) {
        error.value = 'Por favor, informe seu CPF antes de gerar o PIX.'
        return
      }

      processing.value = true
      error.value = null

      try {
        const pixPaymentData = await generatePixPayment({
          participantId: props.participantId,
          ticketId: props.ticketData.id,
          amount: effectiveTotal.value,
          couponId: appliedCoupon.value?.id || null,
          description: props.ticketData.name,
          customer: {
            name: props.customerData.name,
            email: props.customerData.email,
            document: additionalData.value.cpf.replace(/\D/g, ''),
            phone: props.customerData.phone
          }
        })

        pixData.value = pixPaymentData
        emit('pix-generated', pixPaymentData)

        // Inicia polling para monitorar pagamento usando o charge_id (payment_id)
        if (pixPaymentData.payment_id) {
          // Para monitor anterior se existir
          if (pixMonitor.value) {
            pixMonitor.value.stop()
          }

          pixPollingStatus.value = 'polling'
          pixMonitor.value = monitorPixPayment(pixPaymentData.payment_id, (status) => {
            pixPollingStatus.value = status.status
            if (status.status === 'paid') {
              emit('payment-complete', {
                order_id: status.order_id || pixPaymentData.order_id,
                payment_id: pixPaymentData.payment_id,
                payment_method: 'pix'
              })
            }
          })
        }
      } catch (err) {
        error.value = err.message || 'Erro ao gerar PIX. Tente novamente.'
      } finally {
        processing.value = false
      }
    }

    const generateBolet = async () => {
      if (!additionalData.value.cpf) {
        error.value = 'Por favor, informe seu CPF antes de gerar o Boleto.'
        return
      }

      processing.value = true
      error.value = null

      try {
        const boletoPaymentData = await generateBoletoPayment({
          participantId: props.participantId,
          ticketId: props.ticketData.id,
          amount: effectiveTotal.value,
          couponId: appliedCoupon.value?.id || null,
          description: props.ticketData.name,
          customer: {
            name: props.customerData.name,
            email: props.customerData.email,
            document: additionalData.value.cpf.replace(/\D/g, ''),
            phone: props.customerData.phone,
            address: billingAddress.value.line1,
            number: billingAddress.value.number,
            zipcode: billingAddress.value.zipCode.replace(/\D/g, ''),
            city: billingAddress.value.city,
            state: billingAddress.value.state
          }
        })

        boletoData.value = boletoPaymentData
        emit('boleto-generated', boletoPaymentData)
      } catch (err) {
        error.value = err.message || 'Erro ao gerar Boleto. Tente novamente.'
      } finally {
        processing.value = false
      }
    }

    const handleFreePayment = async () => {
      if (!appliedCoupon.value?.id) {
        error.value = 'Cupom gratuito não aplicado corretamente. Reaplique o cupom.'
        return
      }
      processing.value = true
      error.value = null
      try {
        const resp = await processFreePayment({
          participantId: props.participantId,
          ticketId: props.ticketData.id,
          couponId: appliedCoupon.value.id
        })
        emit('payment-complete', {
          order_id: resp.order_id || resp.id || null,
          payment_method: 'free',
          free: true
        })
      } catch (err) {
        error.value = err.message || 'Erro ao confirmar inscrição gratuita. Tente novamente.'
      } finally {
        processing.value = false
      }
    }

    const handlePayment = () => {
      if (isFreeOrder.value) return handleFreePayment()
      if (activePayment.value === 'creditcard') {
        handleCardPayment()
      } else if (activePayment.value === 'pix') {
        generatePix()
      } else if (activePayment.value === 'boleto') {
        generateBolet()
      }
    }

    const copyPixCode = () => {
      navigator.clipboard.writeText(pixData.value.qr_code)
      alert('Código PIX copiado!')
    }

    const applyCoupon = async () => {
      const code = (couponCode.value || '').trim().toUpperCase()
      if (!code) {
        couponError.value = 'Digite um código de cupom.'
        return
      }
      couponValidating.value = true
      couponError.value = null
      try {
        const coupon = await validateCouponApi(code, props.ticketData.id)
        if (!coupon.valid) {
          couponError.value = coupon.message || 'Cupom inválido ou expirado.'
          appliedCoupon.value = null
          return
        }
        appliedCoupon.value = coupon
      } catch (err) {
        couponError.value = err.message || 'Falha ao validar cupom.'
        appliedCoupon.value = null
      } finally {
        couponValidating.value = false
      }
    }

    const removeCoupon = () => {
      appliedCoupon.value = null
      couponCode.value = ''
      couponError.value = null
    }

    return {
      activePayment,
      processing,
      error,
      couponCode,
      appliedCoupon,
      couponValidating,
      couponError,
      discountAmount,
      effectiveTotal,
      isFreeOrder,
      pixData,
      pixPollingStatus,
      boletoData,
      additionalData,
      cardData,
      billingAddress,
      cardErrors,
      installmentOptions,
      serviceFee,
      customerTotal,
      loadingCEP,
      formatCurrency,
      formatCpfCnpj,
      formatCardNumber,
      formatExpiry,
      formatCvv,
      formatZipCode,
      formatState,
      searchCEP,
      handlePayment,
      copyPixCode,
      applyCoupon,
      removeCoupon
    }
  }
}
</script>

<style scoped>
.payment-btn {
  @apply flex flex-col items-center justify-center p-3 border-2 border-gray-200 rounded-md cursor-pointer transition-all bg-white text-gray-700;
}

.payment-btn:hover {
  border-color: var(--button-color, #4A6CF7);
  opacity: 0.8;
}

.payment-btn.active {
  border-color: var(--button-color, #4A6CF7);
  background-color: var(--button-color, #4A6CF7);
  @apply text-white;
}

/* Info badge */
.checkout-info-badge {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(var(--button-color-rgb, 74,108,247), 0.06), rgba(var(--button-color-rgb, 74,108,247), 0.02));
  border: 1px solid rgba(var(--button-color-rgb, 74,108,247), 0.15);
  border-radius: 6px;
  font-size: 13px;
  color: #3D4F6F;
  line-height: 1.5;
}

.checkout-info-badge svg {
  color: var(--button-color, #4A6CF7);
  margin-top: 1px;
}

.checkout-info-badge strong {
  font-weight: 600;
  color: #1B2A4A;
}

/* Primary button */
.checkout-btn-primary {
  background-color: var(--button-color, #4A6CF7);
  color: #fff;
  transition: background-color 0.15s;
}

.checkout-btn-primary:hover:not(:disabled) {
  background-color: var(--button-hover-color, #3B5DE7);
}

/* Link color */
.checkout-link {
  color: var(--button-color, #4A6CF7);
}

.checkout-link:hover {
  color: var(--button-hover-color, #3B5DE7);
}

/* Total price color */
.checkout-total {
  color: var(--button-color, #4A6CF7);
}

/* Spinner */
.checkout-spinner {
  color: var(--button-color, #4A6CF7);
}
</style>
